/**
 * Track 3 — Foreman Field Roll.
 *
 * The single field-facing operational surface. Mobile-first, iPad-first,
 * glove-friendly tap targets, sunlight-friendly contrast.
 *
 * Four sections, in order of operator priority:
 *
 *   1. NEEDS ATTENTION         — unresolved stories, conflicts, clarifications,
 *                                revision acknowledgements. Highest first.
 *   2. WAITING ON YOU          — stories assigned to the current foreman
 *                                that are pending acknowledgement / response /
 *                                resolution.
 *   3. CHANGED SINCE YESTERDAY — new revisions / conflicts / clarifications /
 *                                newly opened stories in the last 24h.
 *   4. CLOSED TODAY            — completed / resolved / acknowledged in
 *                                the last 24h. Trust + momentum.
 *
 * Every card shows: WHAT · WHERE · STATUS · NEXT STEP.
 * Every card supports: Open Story · Jump To Drawing · Acknowledge ·
 * Needs Clarification · Resolve · Send To Superintendent.
 *
 * Reads OperationalStory directly. No duplicate logic, no new collection.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Camera, Mic, Send, ListChecks, CheckCircle2, ShieldAlert,
  MessageSquareWarning, AlertOctagon, ChevronRight, Loader2, RefreshCw,
  ExternalLink, Hammer, Sun, Moon,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";
import StoryDrawer, { KIND, STATUS_TONE, topKind } from "@/components/stories/StoryDrawer";
import { workspace } from "@/lib/workspace";


const CRITICAL_STATUSES = new Set([
  "conflict_open", "waiting_for_clarification", "waiting_for_field",
]);
const TERMINAL_STATUSES = new Set(["closed", "resolved"]);

const PRIORITY = {
  conflict_open: 0,
  waiting_for_clarification: 1,
  waiting_for_field: 2,
  needs_attention: 3,
  waiting_for_review: 4,
  in_progress: 5,
  resolved: 6,
  closed: 7,
};


export default function FieldRoll() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [me, setMe] = useState(null);
  const [selectedId, setSelectedId] = useState(params.get("project_id") || workspace.getLastProject()?.project_id || "");
  const [stories, setStories] = useState(null);
  const [assetIntel, setAssetIntel] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [drawerStoryId, setDrawerStoryId] = useState(null);
  const [theme, setTheme] = useState(() => (localStorage.getItem("fop_theme_pref") || "field_black"));
  const hadInitialSelectedIdRef = useRef(!!selectedId);

  // Sync theme to the doc for sunlight contrast.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("fop_theme_pref", theme);
  }, [theme]);

  // Boot — projects + me.
  useEffect(() => {
    let alive = true;
    api.get("/auth/me").then((r) => alive && setMe(r.data)).catch(() => {});
    api.get("/projects").then((r) => {
      if (!alive) return;
      const list = Array.isArray(r.data) ? r.data : r.data?.projects || [];
      setProjects(list);
      // Mount-only default-selection: use a ref snapshot of whether
      // selectedId already had a value at mount, so this effect can
      // legitimately stay mount-only without omitting a reactive
      // dependency.
      if (!hadInitialSelectedIdRef.current && list.length) setSelectedId(list[0].id);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Load stories whenever the selected project changes.
  const reload = useCallback(() => {
    if (!selectedId) return;
    setRefreshing(true);
    Promise.allSettled([
      api.get(`/projects/${selectedId}/stories`),
      api.get(`/projects/${selectedId}/assets/intelligence`),
    ]).then(([sRes, aRes]) => {
      setStories(sRes.status === "fulfilled" ? (sRes.value.data?.items || []) : []);
      setAssetIntel(aRes.status === "fulfilled" ? aRes.value.data : null);
    }).finally(() => setRefreshing(false));
  }, [selectedId]);
  useEffect(() => {
    if (!selectedId) return;
    setStories(null);
    setParams((p) => { p.set("project_id", selectedId); return p; });
    const projectName = projects.find((p) => p.id === selectedId)?.name;
    if (projectName) workspace.setLastProject({ project_id: selectedId, project_name: projectName });
    reload();
  }, [selectedId, projects, reload, setParams]);

  // Auto-refresh on broadcast.
  useEffect(() => {
    const h = () => reload();
    window.addEventListener("forgedops:field-stories-changed", h);
    return () => window.removeEventListener("forgedops:field-stories-changed", h);
  }, [reload]);

  // Bucket the stories. Critical-first sort inside each.
  const buckets = useMemo(() => {
    const list = stories || [];
    const dayAgo = Date.now() - 86400000;
    const meName = me?.name;

    const isWaitingOnMe = (s) => {
      if (TERMINAL_STATUSES.has(s.status)) return false;
      // Action-level: any open action whose assignee matches me.
      for (const a of (s.open_actions || [])) {
        if (a.assignee_name && a.assignee_name === meName) return true;
      }
      return false;
    };

    const needsAttention = list.filter((s) => !TERMINAL_STATUSES.has(s.status));
    const waitingOnYou   = list.filter((s) => isWaitingOnMe(s));
    const changed = list.filter(
      (s) => s.started_at && new Date(s.started_at).getTime() >= dayAgo,
    );
    const closedToday = list.filter(
      (s) => TERMINAL_STATUSES.has(s.status)
        && s.last_activity_at
        && new Date(s.last_activity_at).getTime() >= dayAgo,
    );

    const byPriority = (a, b) =>
      (PRIORITY[a.status] ?? 9) - (PRIORITY[b.status] ?? 9)
      || (new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0));

    needsAttention.sort(byPriority);
    waitingOnYou.sort(byPriority);
    changed.sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0));
    closedToday.sort((a, b) => new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0));

    return { needsAttention, waitingOnYou, changed, closedToday };
  }, [stories, me]);

  return (
    <div
      className="min-h-screen bg-[var(--fop-bg)] text-[var(--fop-text)] pb-24"
      data-testid="field-roll"
      data-theme={theme}
    >
      <header className="sticky top-0 z-30 bg-[var(--fop-surface)] border-b border-[var(--fop-border)] px-3 py-2 flex items-center gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Hammer size={16} className="text-[var(--fop-primary)] shrink-0" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]" data-testid="field-roll-eyebrow">
              Field Roll
            </div>
            <div className="text-sm font-semibold text-[var(--fop-text)] truncate" data-testid="field-roll-greeting">
              {_greeting()}{me?.name ? `, ${(me.name || "").split(" ")[0]}` : ""}
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            data-testid="field-roll-theme-toggle"
            aria-label="Toggle sunlight theme"
            onClick={() => setTheme((t) => (t === "field_black" ? "daylight" : "field_black"))}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded text-[var(--fop-text-3)] hover:text-[var(--fop-text)] active:bg-[var(--fop-surface-2)]"
          >
            {theme === "field_black" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            type="button"
            data-testid="field-roll-refresh"
            aria-label="Refresh"
            onClick={reload}
            disabled={refreshing}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded text-[var(--fop-text-3)] hover:text-[var(--fop-text)] active:bg-[var(--fop-surface-2)] disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      {/* Project picker — only visible if the foreman touches multiple projects. */}
      {projects.length > 1 && (
        <div className="px-3 py-2 border-b border-[var(--fop-border)] bg-[var(--fop-surface)]">
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] block mb-1" htmlFor="field-roll-project">
            Project
          </label>
          <select
            id="field-roll-project"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="field-roll-project-select"
            className="w-full bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded px-2 py-2 text-[14px] text-[var(--fop-text)]"
          >
            {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
        </div>
      )}

      {stories === null ? (
        <div className="p-8 flex items-center gap-2 text-[var(--fop-text-3)]" data-testid="field-roll-loading">
          <Loader2 className="animate-spin" size={14} /> Loading the field roll…
        </div>
      ) : (
        <main className="px-3 py-3 space-y-3">
          <Section
            id="needs-attention"
            title="Needs Attention"
            icon={<AlertOctagon size={14} className="text-[var(--st-escalated,#dc2626)]" />}
            count={buckets.needsAttention.length}
            empty="Nothing needs attention right now. Field is current."
            rows={buckets.needsAttention}
            onOpen={(s) => setDrawerStoryId(s.id)}
            navigate={navigate}
            onChanged={reload}
          />
          <Section
            id="waiting-on-you"
            title="Waiting On You"
            icon={<MessageSquareWarning size={14} className="text-[var(--fop-warning)]" />}
            count={buckets.waitingOnYou.length}
            empty="Nothing assigned to you right now."
            rows={buckets.waitingOnYou}
            onOpen={(s) => setDrawerStoryId(s.id)}
            navigate={navigate}
            onChanged={reload}
            highlight="waiting"
          />
          <Section
            id="changed-since-yesterday"
            title="Changed Since Yesterday"
            icon={<RefreshCw size={14} className="text-[var(--fop-primary)]" />}
            count={buckets.changed.length}
            empty="No new field activity in the last 24 hours."
            rows={buckets.changed}
            onOpen={(s) => setDrawerStoryId(s.id)}
            navigate={navigate}
            onChanged={reload}
          />
          <Section
            id="closed-today"
            title="Closed Today"
            icon={<CheckCircle2 size={14} className="text-[var(--fop-success)]" />}
            count={buckets.closedToday.length}
            empty="Nothing closed today yet — keep going."
            rows={buckets.closedToday}
            onOpen={(s) => setDrawerStoryId(s.id)}
            navigate={navigate}
            onChanged={reload}
            closedView
          />
          {/* Track 4 — Asset summary as a separate read-only section. */}
          <section
            data-testid="field-roll-section-assets"
            className="rounded-lg border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden"
          >
            <header className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-2 bg-[var(--fop-surface-2)]">
              <Hammer size={14} className="text-[var(--fop-primary)]" />
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-text-2)] font-semibold">
                Assets On This Job
              </div>
              <div className="ml-auto font-mono text-[11px] tabular-nums text-[var(--fop-text-3)] font-semibold" data-testid="field-roll-section-assets-count">
                {assetIntel?.total || 0}
              </div>
            </header>
            {(assetIntel?.total || 0) === 0 ? (
              <div className="p-4 text-[13px] text-[var(--fop-text-3)]" data-testid="field-roll-section-assets-empty">
                No assets tagged yet. Open a sheet to place one.
              </div>
            ) : (
              <div className="p-3 grid grid-cols-2 gap-2" data-testid="field-roll-section-assets-grid">
                {(assetIntel.by_status || []).slice(0, 6).map((b) => (
                  <Link
                    key={b.status}
                    to={`/quantity-intelligence?project_id=${selectedId}`}
                    className="block rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] px-3 py-2 hover:bg-[var(--fop-surface-2)]"
                    data-testid={`field-roll-asset-status-${b.status}`}
                  >
                    <div className="font-mono text-2xl tabular-nums text-[var(--fop-text)] font-bold">{b.count}</div>
                    <div className="text-[11px] text-[var(--fop-text-3)] font-mono uppercase tracking-wider">{b.label}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </main>
      )}

      <StoryDrawer
        open={!!drawerStoryId}
        storyId={drawerStoryId}
        onClose={() => setDrawerStoryId(null)}
      />
    </div>
  );
}


function Section({ id, title, icon, count, empty, rows, onOpen, navigate, onChanged, closedView, highlight }) {
  return (
    <section
      data-testid={`field-roll-section-${id}`}
      className="rounded-lg border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden"
    >
      <header className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-2 bg-[var(--fop-surface-2)]">
        {icon}
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-text-2)] font-semibold">
          {title}
        </div>
        <div className="ml-auto font-mono text-[11px] tabular-nums text-[var(--fop-text-3)] font-semibold" data-testid={`field-roll-section-${id}-count`}>
          {count}
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="p-4 text-[13px] text-[var(--fop-text-3)]" data-testid={`field-roll-section-${id}-empty`}>
          {empty}
        </div>
      ) : (
        <ul>
          {rows.slice(0, 50).map((s) => (
            <StoryCard
              key={s.id}
              story={s}
              onOpen={onOpen}
              navigate={navigate}
              onChanged={onChanged}
              testid={`field-roll-card-${s.id}`}
              closedView={closedView}
              highlight={highlight}
            />
          ))}
        </ul>
      )}
    </section>
  );
}


function StoryCard({ story, onOpen, navigate, onChanged, testid, closedView, highlight }) {
  const top = topKind(story);
  const meta = KIND[top] || KIND.photo;
  const Icon = meta.icon;
  const statusColour = STATUS_TONE[story.status] || meta.colour;
  const isCritical = CRITICAL_STATUSES.has(story.status);
  const nextAction = (story.open_actions || [])[0];

  return (
    <li
      data-testid={testid}
      className={`relative border-b border-[var(--fop-border)] last:border-b-0 ${
        isCritical ? "bg-[rgba(220,38,38,0.04)]" : ""
      }`}
    >
      {isCritical && (
        <div
          className="absolute top-0 bottom-0 left-0 w-1"
          style={{ background: statusColour }}
          aria-hidden
        />
      )}
      <button
        type="button"
        onClick={() => onOpen(story)}
        className="w-full text-left flex items-start gap-3 px-3 py-3 active:bg-[var(--fop-surface-2)] transition-colors"
        data-testid={`${testid}-open`}
        aria-label="Open story"
      >
        <div
          className="mt-0.5 inline-flex items-center justify-center rounded-full shrink-0"
          style={{ width: 32, height: 32, background: "var(--fop-surface)",
                   color: meta.colour, border: `2px solid ${meta.colour}66` }}
        >
          <Icon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          {/* WHAT */}
          <div className="text-[14px] font-semibold text-[var(--fop-text)] truncate" data-testid={`${testid}-what`}>
            {meta.label} on {story.sheet_number || "this sheet"}
          </div>
          {/* WHERE + STATUS */}
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] font-semibold"
              style={{ background: "rgba(0,0,0,0.04)", color: statusColour, border: `1px solid ${statusColour}55` }}
              data-testid={`${testid}-status`}
            >
              {story.status_label || story.status || "Needs Attention"}
            </span>
            <span className="font-mono text-[11px] text-[var(--fop-text-3)] uppercase tracking-wider">
              {closedView ? "closed " : ""}{_ageLabel(story.last_activity_at)}
            </span>
            {(story.people || []).length > 0 && (
              <span className="font-mono text-[11px] text-[var(--fop-text-3)] truncate">
                · {story.people.join(" · ")}
              </span>
            )}
          </div>
          {/* NEXT STEP */}
          {nextAction && !closedView && (
            <div
              className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-[var(--fop-text-2)]"
              data-testid={`${testid}-next`}
            >
              <ChevronRight size={12} className="text-[var(--fop-text-3)] shrink-0" />
              <span className="truncate">{nextAction.label}</span>
            </div>
          )}
        </div>
        <ChevronRight size={14} className="text-[var(--fop-text-3)] shrink-0 mt-1" />
      </button>

      {/* Glove-friendly inline actions. Only render those relevant to this story. */}
      {!closedView && (
        <div className="px-3 pb-3 flex gap-2 flex-wrap" data-testid={`${testid}-actions`}>
          <ActionPill
            visible={!!nextAction}
            label={nextAction?.kind === "send_to_foreman" ? "Send" :
                   nextAction?.kind === "acknowledge_revision" ? "Acknowledge" :
                   nextAction?.kind === "resolve_clarification" ? "Resolve" :
                   nextAction?.kind === "resolve_conflict" ? "Resolve" :
                   nextAction?.kind === "complete_field_work" ? "Mark complete" : "Open"}
            onClick={() => _runAction(nextAction, navigate, onChanged)}
            kind="primary"
            testid={`${testid}-act-primary`}
          />
          <ActionPill
            visible={canClarify(story)}
            label="Needs Clarification"
            onClick={() => _runClarify(story, navigate)}
            testid={`${testid}-act-clarify`}
          />
          <ActionPill
            visible={canSendUp(story)}
            label="Send to Super"
            onClick={() => _runSendUp(story, navigate)}
            testid={`${testid}-act-send-up`}
          />
          <Link
            to={`/sheet/${story.sheet_id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center gap-1 px-3 rounded border border-[var(--fop-border)] text-[13px] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] font-semibold"
            style={{ minHeight: 44 }}
            data-testid={`${testid}-act-jump`}
          >
            <ExternalLink size={12} /> Drawing
          </Link>
        </div>
      )}
    </li>
  );
}


function ActionPill({ visible, label, onClick, kind, testid }) {
  if (!visible) return null;
  const cls = kind === "primary"
    ? "border-[var(--fop-primary)]/60 bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
    : "border-[var(--fop-border)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-1 px-3 rounded border text-[13px] font-semibold ${cls}`}
      style={{ minHeight: 44 }}
    >
      {label}
    </button>
  );
}


function canClarify(s) {
  // A foreman can mark "Needs Clarification" on stories where an ack is
  // still pending or where the chain is open and an ack member exists.
  if (TERMINAL_STATUSES.has(s.status)) return false;
  return (s.members || []).some((m) =>
    m.kind === "foreman_ack" || m.kind === "clarification_open"
    || m.kind === "revision_send",
  );
}

function canSendUp(s) {
  if (TERMINAL_STATUSES.has(s.status)) return false;
  return s.status !== "waiting_for_review"; // only after at least one action
}


async function _runAction(action, navigate, onChanged) {
  if (!action) return;
  try {
    if (action.kind === "send_to_foreman") {
      const path = action.source_kind === "photo"
        ? `/field-photos/${action.source_id}/send-to-foreman`
        : `/field-voice-notes/${action.source_id}/send-to-foreman`;
      await api.post(path, {});
      window.dispatchEvent(new Event("forgedops:field-stories-changed"));
      onChanged?.();
      confirm.raw?.success?.("Sent");
    } else {
      // Everything else lives in the inbox flow today.
      navigate(`/inbox?id=${action.source_id}`);
    }
  } catch (e) {
    confirm.raw?.error?.(e?.response?.data?.detail || "Action failed");
  }
}

function _runClarify(s, navigate) {
  // Route to the inbox where the foreman can post the clarification.
  // Prefer the ack member as the inbox item id.
  const ackId = (s.members || []).find((m) =>
    ["foreman_ack", "clarification_open"].includes(m.kind))?.id;
  navigate(`/inbox?${ackId ? `id=${ackId}&` : ""}clarify=1`);
}

function _runSendUp(s, navigate) {
  // No dedicated endpoint — surface the story to the super by routing to
  // the drawing (the super already watches War Room + Command View, which
  // both consume the same story state).
  navigate(`/sheet/${s.sheet_id}`);
}


function _greeting() {
  const h = new Date().getHours();
  if (h < 5)  return "Late shift";
  if (h < 11) return "Good morning";
  if (h < 17) return "Afternoon";
  return "Evening";
}

function _ageLabel(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60000) return "now";
    const m = Math.round(ms / 60000);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
  } catch { return ""; }
}
